import React, { useState, useEffect } from 'react';
import useWebSocket from '../../hooks/wsHook';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAtom } from 'jotai';
import { sensorInfoAtom, SensorInfo, ParsedSensorInfo, Weather, JInfo, Esensor } from '../atoms-types/eSensors';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Card, Typography, CardContent, Box, Divider, useTheme, useMediaQuery } from '@mui/material';
import EsensorChartHandler from '../handlers/eSensorGraph-handler';
import { endpointLink, websocketLink } from '../../index';
import SensorIdentityTooltip from '../hvac/SensorIdentityTooltip';

const ResponsiveGridLayout = WidthProvider(Responsive);

type Layout = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  minH: number;
};

const TARGET_EUI = '2CF7F1C06090023A';

export const WeatherGrid: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [sensorInfo, setSensorInfo] = useAtom(sensorInfoAtom);
  const [selectedSensor, setSelectedSensor] = useState<{ deviceEUI: string; display: string[] }>({
    deviceEUI: TARGET_EUI,
    display: ['TempC', 'Humidity', 'WindAvg']
  });
  const { messages } = useWebSocket(websocketLink, ['sensor']);

  // WebSocket updates
  useEffect(() => {
    console.log('WeatherGrid: Received messages:', messages);
    messages.forEach((messageObj) => {
      const { channel, message } = messageObj;
      try {
        if (channel === 'sensor' && message) {
          const { deviceEUI, ...jInfo } = message as Esensor;
          
          // Only process messages from our target EUI
          if (deviceEUI !== TARGET_EUI) {
            return;
          }
          
          console.log('WeatherGrid: Updating sensor info with new data:', jInfo);
          setSensorInfo((prev) => {
            if (!prev[deviceEUI]) {
              return prev;
            }
            
            // Compare new values with existing values
            const currentSensor = prev[deviceEUI][0];
            if (currentSensor && currentSensor.jInfo) {
              const changes: { [key: string]: { old: any; new: any } } = {};
              Object.entries(jInfo).forEach(([key, newValue]) => {
                const oldValue = (currentSensor.jInfo as any)[key];
                if (oldValue !== newValue) {
                  changes[key] = { old: oldValue, new: newValue };
                }
              });
              
              if (Object.keys(changes).length > 0) {
                console.log('WeatherGrid: Detected value changes:', changes);
              } else {
                console.log('WeatherGrid: No value changes detected in new message');
              }
            }
            
            const updatedSensors = prev[deviceEUI].map(sensor => ({
              ...sensor,
              jInfo
            }));

            return {
              ...prev,
              [deviceEUI]: updatedSensors
            };
          });
        }
      } catch (error) {
        console.error('WeatherGrid: Failed to process websocket message:', error);
      }
    });
  }, [messages]);

  // Query for sensor data
  const { data, isLoading } = useQuery({
    queryKey: ['sensorMetadata'],
    staleTime: Infinity,
    queryFn: async () => {
      console.log('WeatherGrid: Fetching initial sensor data');
      const response = await axios.get<SensorInfo[]>(`${endpointLink}sensor/weather`);
      const data = response.data;
      console.log('WeatherGrid: Received initial sensor data:', data);
      
      // Filter for weather sensors only
      const weatherSensors = data.filter(sensor => sensor.Type === 'Weather');
      console.log('WeatherGrid: Filtered weather sensors:', weatherSensors);
      
      // Update the sensorInfo atom with data
      const newSensorInfo: { [deviceEUI: string]: SensorInfo[] } = {};
      weatherSensors.forEach(sensor => {
        if (!newSensorInfo[sensor.deviceEUI]) {
          newSensorInfo[sensor.deviceEUI] = [];
        }
        newSensorInfo[sensor.deviceEUI].push(sensor);
      });
      
      console.log('WeatherGrid: Updating sensor info with initial data:', newSensorInfo);
      updateSensorInfo(newSensorInfo);
      
      return weatherSensors;
    }
  });

  const updateSensorInfo = (newSensorInfo: { [deviceEUI: string]: SensorInfo[] }) => { 
    const updatedSensorInfo: { [deviceEUI: string]: ParsedSensorInfo[] } = {};
    
    Object.entries(newSensorInfo).forEach(([deviceEUI, sensors]) => {
      updatedSensorInfo[deviceEUI] = sensors.map(sensor => {
        try {
          const parsedSensor = {
            ...sensor,
            Class: typeof sensor.Class === 'string' ? JSON.parse(sensor.Class) : sensor.Class,
            Display: typeof sensor.Display === 'string' ? JSON.parse(sensor.Display) : sensor.Display,
            jInfo: typeof sensor.jInfo === 'string' ? JSON.parse(sensor.jInfo) : sensor.jInfo
          };
          return parsedSensor as ParsedSensorInfo;
        } catch (error) {
          console.error(`Error parsing sensor data for deviceEUI ${deviceEUI}:`, error);
          const defaultSensor = {
            ...sensor,
            Class: typeof sensor.Class === 'string' ? JSON.parse(sensor.Class) : sensor.Class,
            Display: typeof sensor.Display === 'string' ? JSON.parse(sensor.Display) : sensor.Display,
            jInfo: {} as JInfo
          };
          return defaultSensor as ParsedSensorInfo;
        }
      });
    });
    
    setSensorInfo(updatedSensorInfo);
  }

  // Get the unit based on the display type
  const getUnit = (displayType: string) => {
    switch (displayType) {
      case 'TempC':
        return '°C';
      case 'TempF':
        return '°F';
      case 'Humidity':
        return '%';
      case 'WindAvg':
      case 'WindMax':
        return 'mph';
      case 'WindDir':
        return '°';
      case 'BaroPress':
        return 'hPa';
      default:
        return '';
    }
  };

  // Get the color based on the display type
  const getColor = (displayType: string) => {
    switch (displayType) {
      case 'TempC':
      case 'TempF':
      case 'Humidity':
        return 'success.main';
      case 'WindAvg':
      case 'WindMax':
      case 'WindDir':
      case 'BaroPress':
        return 'info.main';
      default:
        return 'primary.main';
    }
  };

  // Create layout
  const createLayout = (): Layout[] => {
    if (!sensorInfo) return [];

    const layouts: Layout[] = [];
    const deviceEUIs = Object.keys(sensorInfo);
    
    if (deviceEUIs.length > 0) {
      // Add temperature/humidity card
      layouts.push({
        i: `${deviceEUIs[0]}-temp`,
        x: 0,
        y: 0,
        w: 3,
        h: 4,
        minW: 2,
        minH: 4
      });

      // Add wind/pressure card
      layouts.push({
        i: `${deviceEUIs[0]}-wind`,
        x: 3,
        y: 0,
        w: 3,
        h: 4,
        minW: 2,
        minH: 4
      });

      // Add chart below
      layouts.push({
        i: 'WeatherChart',
        x: 0,
        y: 4,
        w: 9,
        h: 6,
        minW: 6,
        minH: 6
      });
    }

    return layouts;
  };

  const layout = createLayout();

  // Filter displays based on type
  const filterDisplays = (displays: string[], type: 'temp' | 'wind') => {
    if (type === 'temp') {
      return displays.filter(d => ['TempC', 'TempF', 'Humidity'].includes(d));
    } else {
      return displays.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));
    }
  };

  // Create a card component
  const createCard = (layoutId: string) => {
    if (!data || data.length === 0) return null;

    // Special case for chart
    if (layoutId === 'WeatherChart') {
      return (
        <div key="WeatherChart" style={{ minHeight: "500px" }} className='card-gauge'>
          <Card 
            sx={{ 
              minHeight: "500px", 
              width: "100%",
              bgcolor: 'background.paper',
              color: 'text.primary'
            }}
          >
            <EsensorChartHandler 
              deviceEUI={selectedSensor.deviceEUI} 
              Display={selectedSensor.display} 
            />
          </Card>
        </div>
      );
    }

    // Parse layoutId to get deviceEUI and card type
    const [deviceEUI, cardType] = layoutId.split('-');
    
    // Find the sensor metadata
    const sensors = sensorInfo[deviceEUI];
    if (!sensors || sensors.length === 0) return null;
    const sensor = sensors[0];

    // Get the value from the sensor's jInfo
    const sensorData = sensor.jInfo;
    if (!sensorData) return null;

    // Ensure Display is treated as an array of strings and filter based on card type
    const displays = Array.isArray(sensor.Display) ? sensor.Display : [];
    const filteredDisplays = filterDisplays(displays, cardType as 'temp' | 'wind');

    if (filteredDisplays.length === 0) return null;

    return (
      <div 
        key={layoutId}
        style={{ minHeight: "150px" }} 
        className='card-gauge'
      >
        <Card 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            width: '100%', 
            height: '100%',
            overflow: 'visible',
            bgcolor: 'background.paper',
            color: 'text.primary',
            cursor: 'default'
          }}
        >
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredDisplays.map((displayType: string) => {
                const value = (sensorData as Weather)[displayType as keyof Weather];
                const valueKey = `${layoutId}-${displayType}-${value}`;
                return (
                  <Box 
                    key={valueKey}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      px: 1
                    }}
                  >
                    {/* Measurement label (e.g. "TempC:", "Humidity:") */}
                    <Typography variant="h3" sx={{ color: theme.palette.text.secondary }}>
                      {displayType}:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                      {/* Measurement value (e.g. "25.5", "60") */}
                      <Typography 
                        key={`value-${valueKey}`}
                        variant="h3" 
                        sx={{ color: getColor(displayType) }}
                      >
                        {typeof value === 'number' ? value.toFixed(2) : value}
                      </Typography>
                      {/* Unit (e.g. "°C", "%", "mph") */}
                      <Typography 
                        key={`unit-${valueKey}`}
                        variant="h5" 
                        sx={{ color: getColor(displayType), ml: 0.5 }}
                      >
                        {getUnit(displayType)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ borderBottomWidth: '3px', borderColor: filteredDisplays.length > 0 ? getColor(filteredDisplays[0]) : 'primary.main' }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                {/* Card title (e.g. "Temperature & Humidity" or "Wind & Pressure") */}
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: theme.palette.text.secondary
                  }}
                >
                  {cardType === 'temp' ? 'Temperature & Humidity' : 'Wind & Pressure'}
                </Typography>
                {(() => {
                  const loc = (sensor.Location || '').trim();
                  const fallback = (sensor.deviceID || '').trim();
                  const display = loc && loc !== '---' ? loc : fallback;
                  if (!display) return null;
                  return (
                    <>
                      {/* Bullet point separator */}
                      <Typography variant="h4" sx={{ color: theme.palette.text.secondary }}>•</Typography>
                      {/* Location text */}
                      <SensorIdentityTooltip sensor={sensor}>
                        <Typography
                          variant="h4"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {display}
                        </Typography>
                      </SensorIdentityTooltip>
                    </>
                  );
                })()}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        height: '100vh',
        width: '100%',
        overflowY: isSmallScreen ? 'auto' : 'visible',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      <Box sx={{ minHeight: '100%', width: '100%' }}>
        <ResponsiveGridLayout 
          className="layout" 
          layouts={{ lg: layout }} 
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
          cols={{ lg: 9, md: 6, sm: 3, xs: 1 }}
          rowHeight={100}
          draggableCancel=".cancelSelectorName"
          isDraggable={false}
          isResizable={false}
        >
          {layout.map(l => createCard(l.i))}
        </ResponsiveGridLayout>
      </Box>
    </Box>
  );
};

export default WeatherGrid; 